import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { nutritionAPI } from '../../services/api'
import TodaySummaryCard from '../nutrition/TodaySummaryCard'

export default function DailyNutritionTab() {
  const { t } = useTranslation()
  const today = new Date().toISOString().split('T')[0]
  const [expandedWeek, setExpandedWeek] = useState(null)
  const [weeksToLoad, setWeeksToLoad] = useState(4)

  const { data: mealsData, isLoading } = useQuery({
    queryKey: ['todayMeals', today],
    queryFn: () => nutritionAPI.getMealsByDate(today)
  })

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['nutritionWeeklyHistory', weeksToLoad],
    queryFn: () => nutritionAPI.getWeeklyHistory(weeksToLoad),
    select: (res) => res.data,
  })

  const meals = mealsData?.data?.meals || mealsData?.data || []
  const weeklyHistory = historyData || []

  const todayTotals = meals.reduce(
    (acc, meal) => ({
      calories: acc.calories + (meal.total_calories || 0),
      protein: acc.protein + (meal.total_protein_g || 0),
      carbs: acc.carbs + (meal.total_carbs_g || 0),
      fat: acc.fat + (meal.total_fat_g || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  )

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const formatWeekLabel = (weekStart, weekEnd) => {
    const start = new Date(weekStart + 'T00:00:00')
    const end = new Date(weekEnd + 'T00:00:00')
    return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
  }

  return (
    <div className="space-y-6">
      {/* Today Summary */}
      <TodaySummaryCard
        calories={Math.round(todayTotals.calories)}
        protein={Math.round(todayTotals.protein)}
        carbs={Math.round(todayTotals.carbs)}
        fat={Math.round(todayTotals.fat)}
        mealCount={meals.length}
      />

      {/* Multi-Week History */}
      {historyLoading ? (
        <div className="card"><div className="card-body animate-pulse"><div className="h-32 bg-gray-200 rounded"></div></div></div>
      ) : weeklyHistory.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">{t('nutritionTab.weeklyHistory', 'Weekly History')}</h4>
          {weeklyHistory.map((week, idx) => {
            const isExpanded = expandedWeek === idx
            const totals = week.totals || {}
            const averages = week.averages || {}
            const targets = week.targets || {}
            const hasMeals = (totals.total_meals || 0) > 0
            const daysLogged = week.days_logged || 0

            return (
              <div key={idx} className="card overflow-hidden">
                <div
                  className="card-body cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedWeek(isExpanded ? null : idx)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {formatWeekLabel(week.week_start, week.week_end)}
                      </p>
                      {!hasMeals && (
                        <p className="text-xs text-gray-400">{t('nutritionTab.noMeals', 'No meals logged')}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      {hasMeals && (
                        <div className="flex gap-4 text-sm">
                          <span className="text-indigo-600 font-semibold">
                            {Math.round(averages.daily_calories || 0)} <span className="text-xs font-normal text-gray-400">kcal/d</span>
                          </span>
                          <span className="text-green-600 font-semibold hidden sm:inline">
                            {Math.round(averages.daily_protein_g || 0)}g <span className="text-xs font-normal text-gray-400">P/d</span>
                          </span>
                          <span className="text-amber-600 font-semibold hidden md:inline">
                            {totals.total_meals || 0} <span className="text-xs font-normal text-gray-400">{t('nutritionTab.meals', 'meals')}</span>
                          </span>
                        </div>
                      )}
                      {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                    </div>
                  </div>

                  {/* Expanded: totals + averages */}
                  {isExpanded && hasMeals && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      {/* Totals row */}
                      <p className="text-xs font-medium text-gray-500 mb-2">
                        {t('nutritionTab.weekTotals', 'Week Totals')}
                        {daysLogged > 0 && (
                          <span className="ml-1 font-normal text-gray-400">
                            {t('nutritionTab.daysLogged', '({{count}} of 7 days logged)', { count: daysLogged })}
                          </span>
                        )}
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        <div className="bg-indigo-50 rounded-lg p-2 text-center">
                          <p className="text-lg font-bold text-indigo-600">{Math.round(totals.total_calories || 0)}</p>
                          <p className="text-xs text-gray-500">kcal</p>
                          {targets.weekly_calories > 0 && (
                            <p className="text-xs text-gray-400">
                              {t('nutritionTab.ofTarget', 'of {{target}}', { target: Math.round(targets.weekly_calories) })}
                            </p>
                          )}
                        </div>
                        <div className="bg-green-50 rounded-lg p-2 text-center">
                          <p className="text-lg font-bold text-green-600">{Math.round(totals.total_protein_g || 0)}g</p>
                          <p className="text-xs text-gray-500">{t('nutritionTab.protein', 'Protein')}</p>
                          {targets.weekly_protein_g > 0 && (
                            <p className="text-xs text-gray-400">
                              {t('nutritionTab.ofTargetGrams', 'of {{target}}g', { target: Math.round(targets.weekly_protein_g) })}
                            </p>
                          )}
                        </div>
                        <div className="bg-blue-50 rounded-lg p-2 text-center">
                          <p className="text-lg font-bold text-blue-600">{Math.round(totals.total_carbs_g || 0)}g</p>
                          <p className="text-xs text-gray-500">{t('nutritionTab.carbs', 'Carbs')}</p>
                          {targets.weekly_carbs_g > 0 && (
                            <p className="text-xs text-gray-400">
                              {t('nutritionTab.ofTargetGrams', 'of {{target}}g', { target: Math.round(targets.weekly_carbs_g) })}
                            </p>
                          )}
                        </div>
                        <div className="bg-amber-50 rounded-lg p-2 text-center">
                          <p className="text-lg font-bold text-amber-600">{Math.round(totals.total_fat_g || 0)}g</p>
                          <p className="text-xs text-gray-500">{t('nutritionTab.fat', 'Fat')}</p>
                          {targets.weekly_fat_g > 0 && (
                            <p className="text-xs text-gray-400">
                              {t('nutritionTab.ofTargetGrams', 'of {{target}}g', { target: Math.round(targets.weekly_fat_g) })}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Averages row */}
                      <p className="text-xs font-medium text-gray-500 mb-2">
                        {t('nutritionTab.dailyAvg', 'Daily Average')}
                        {daysLogged > 0 && (
                          <span className="ml-1 font-normal text-gray-400">
                            {t('nutritionTab.overLoggedDays', '(over {{count}} logged days)', { count: daysLogged })}
                          </span>
                        )}
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="text-center">
                          <p className="text-sm font-semibold text-indigo-600">{Math.round(averages.daily_calories || 0)}</p>
                          <p className="text-xs text-gray-400">kcal/d</p>
                          {targets.daily_calories > 0 && (
                            <p className="text-xs text-gray-400">
                              {t('nutritionTab.ofTarget', 'of {{target}}', { target: Math.round(targets.daily_calories) })}
                            </p>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-green-600">{Math.round(averages.daily_protein_g || 0)}g</p>
                          <p className="text-xs text-gray-400">P/d</p>
                          {targets.daily_protein_g > 0 && (
                            <p className="text-xs text-gray-400">
                              {t('nutritionTab.ofTargetGrams', 'of {{target}}g', { target: Math.round(targets.daily_protein_g) })}
                            </p>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-blue-600">{Math.round(averages.daily_carbs_g || 0)}g</p>
                          <p className="text-xs text-gray-400">C/d</p>
                          {targets.daily_carbs_g > 0 && (
                            <p className="text-xs text-gray-400">
                              {t('nutritionTab.ofTargetGrams', 'of {{target}}g', { target: Math.round(targets.daily_carbs_g) })}
                            </p>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-amber-600">{Math.round(averages.daily_fat_g || 0)}g</p>
                          <p className="text-xs text-gray-400">F/d</p>
                          {targets.daily_fat_g > 0 && (
                            <p className="text-xs text-gray-400">
                              {t('nutritionTab.ofTargetGrams', 'of {{target}}g', { target: Math.round(targets.daily_fat_g) })}
                            </p>
                          )}
                        </div>
                      </div>

                      {!targets.has_goal && (
                        <p className="mt-3 text-xs text-gray-400">
                          {t('nutritionTab.noTarget', 'No nutrition targets set — add them in your profile to compare against your goals.')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {/* Load more */}
          {weeksToLoad < 12 && (
            <button
              onClick={() => setWeeksToLoad((w) => Math.min(w + 4, 12))}
              className="w-full text-sm text-indigo-600 hover:text-indigo-800 py-2"
            >
              {t('nutritionTab.loadMore', 'Load more weeks')}
            </button>
          )}
        </div>
      )}

      {/* Link to full Nutrition page */}
      <div className="text-center">
        <a
          href="/nutrition"
          className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800"
        >
          {t('myProgress.goToNutrition', 'Go to full Nutrition page')}
          <ArrowRightIcon className="h-4 w-4 ml-1" />
        </a>
      </div>
    </div>
  )
}
